import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, ChevronLeft, ChevronRight, Filter, FileSpreadsheet, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

// Color map per code
const CODE_COLOR = {
  P:  'bg-emerald-100 text-emerald-800 border-emerald-200',
  AL: 'bg-violet-100 text-violet-800 border-violet-200',
  SL: 'bg-rose-100 text-rose-800 border-rose-200',
  PH: 'bg-sky-100 text-sky-800 border-sky-200',
  UL: 'bg-amber-100 text-amber-800 border-amber-200',
  ML: 'bg-pink-100 text-pink-800 border-pink-200',
  AB: 'bg-red-200 text-red-900 border-red-300',
  A:  'bg-red-200 text-red-900 border-red-300',
  O:  'bg-gray-200 text-gray-700 border-gray-300',
  NP: 'bg-orange-100 text-orange-800 border-orange-200',
  LT: 'bg-amber-200 text-amber-900 border-amber-300',
};

const CODES = ['', 'P', 'O', 'AL', 'SL', 'PH', 'UL', 'ML', 'AB', 'A', 'NP', 'LT'];

const AttendanceMatrix = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [department, setDepartment] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // {rowId, date, code}
  const [exporting, setExporting] = useState('');

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year, month };
      if (department) params.department = department;
      const res = await axios.get(`${API}/api/clock/admin/attendance`, { params, headers });
      setData(res.data);
    } catch {
      toast({ title: 'Failed to load attendance', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [year, month, department, headers]);

  useEffect(() => { load(); }, [load]);

  const departments = useMemo(() => {
    if (!data) return [];
    const set = new Set();
    (data.rows || []).forEach(r => r.department && set.add(r.department));
    return Array.from(set).sort();
  }, [data]);

  const changeMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const saveCell = async (employeeId, date, newCode) => {
    try {
      await axios.put(`${API}/api/clock/admin/attendance`,
        { employee_id: employeeId, date, code: newCode || '' },
        { headers }
      );
      // Optimistic update
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.map(r => r.employee_id === employeeId ? {
            ...r,
            cells: {
              ...r.cells,
              [date]: { code: newCode || (r.cells[date]?.source === 'leave' || r.cells[date]?.source === 'clock' ? r.cells[date]?.code : ''), source: newCode ? 'manual' : (r.cells[date]?.source || '') },
            },
          } : r),
        };
      });
      // Re-fetch in background to ensure auto-detect re-evaluates blanks
      if (!newCode) load();
    } catch {
      toast({ title: 'Failed to save', variant: 'destructive' });
    }
  };

  const onCellChange = (rowId, date, newCode) => {
    saveCell(rowId, date, newCode);
  };

  const filteredRows = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data.rows;
    const q = search.trim().toLowerCase();
    return data.rows.filter(r =>
      (r.employee_name || '').toLowerCase().includes(q) ||
      (r.employee_code || '').toLowerCase().includes(q) ||
      (r.department || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const monthName = new Date(year, month - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });

  const downloadExport = async (format) => {
    setExporting(format);
    try {
      const params = { year, month, format };
      if (department) params.department = department;
      const res = await axios.get(`${API}/api/clock/admin/attendance/export`, {
        params,
        headers,
        responseType: 'blob',
      });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const blob = new Blob([res.data], {
        type: format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_${year}_${String(month).padStart(2, '0')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: `Failed to export ${format.toUpperCase()}`, variant: 'destructive' });
    } finally {
      setExporting('');
    }
  };

  return (
    <Card data-testid="attendance-matrix-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} data-testid="att-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
            <CardTitle className="text-base min-w-[180px] text-center">{monthName}</CardTitle>
            <Button variant="outline" size="icon" onClick={() => changeMonth(1)} data-testid="att-next-month"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <Select value={department || 'ALL'} onValueChange={(v) => setDepartment(v === 'ALL' ? '' : v)}>
              <SelectTrigger className="w-44 h-9"><SelectValue placeholder="All departments" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All departments</SelectItem>
                {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              placeholder="Search staff…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-48 h-9"
              data-testid="att-search"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadExport('excel')}
              disabled={!!exporting || loading || !data}
              data-testid="att-export-excel"
              className="h-9 gap-1.5"
            >
              {exporting === 'excel'
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <FileSpreadsheet className="h-4 w-4 text-emerald-600" />}
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadExport('pdf')}
              disabled={!!exporting || loading || !data}
              data-testid="att-export-pdf"
              className="h-9 gap-1.5"
            >
              {exporting === 'pdf'
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <FileText className="h-4 w-4 text-rose-600" />}
              PDF
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-[10px] mt-2">
          {data && Object.entries(data.legend).map(([code, label]) => (
            <span key={code} className={`px-2 py-0.5 rounded border ${CODE_COLOR[code] || 'bg-gray-100'}`}>
              <strong>{code}</strong> <span className="opacity-80">{label}</span>
            </span>
          ))}
          <span className="text-gray-500 ml-2">Pick a code from each cell's dropdown. Auto-filled cells (P/AL/PH/SL/ML/UL) come from clock + leave records.</span>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-gray-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
        ) : !data || filteredRows.length === 0 ? (
          <div className="py-10 text-center text-gray-500 text-sm">No staff found for this month/filter.</div>
        ) : (
          <div className="overflow-auto border rounded-lg max-h-[70vh]">
            <table className="text-xs border-collapse">
              <thead className="bg-gray-50 sticky top-0 z-30">
                <tr>
                  <th className="text-left px-2 py-1.5 border-b border-r sticky left-0 top-0 bg-gray-50 z-40 min-w-[180px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">Staff</th>
                  <th className="text-left px-2 py-1.5 border-b border-r sticky top-0 bg-gray-50 z-30 min-w-[60px]">Dept</th>
                  {data.days.map(d => (
                    <th key={d.date} className={`px-1 py-1 border-b text-center text-[10px] font-medium sticky top-0 z-20 ${d.is_weekend ? 'bg-gray-100 text-gray-500' : 'bg-gray-50 text-gray-600'}`} title={d.date}>
                      <div>{d.dow}</div>
                      <div className="text-[11px] font-semibold text-gray-700">{d.day}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(r => (
                  <tr key={r.employee_id} className="group hover:bg-violet-50/30">
                    <td className="px-2 py-1 border-b border-r sticky left-0 bg-white z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] group-hover:bg-violet-50/30" data-testid={`att-name-${r.employee_code}`}>
                      <div className="font-medium text-gray-800 text-[11px]">{r.employee_name}</div>
                      <div className="text-[10px] text-gray-500">{r.employee_code}</div>
                    </td>
                    <td className="px-2 py-1 border-b border-r text-[10px] text-gray-600">{r.department || '—'}</td>
                    {data.days.map(d => {
                      const cell = r.cells[d.date] || { code: '', source: '' };
                      const color = cell.code ? (CODE_COLOR[cell.code] || 'bg-gray-100 text-gray-700 border-gray-200') : 'bg-white text-gray-400 border-gray-100';
                      const isManual = cell.source === 'manual';
                      const fmtT = (iso) => {
                        if (!iso) return '';
                        try {
                          return new Date(iso).toLocaleTimeString('en-GB', {
                            timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit', hour12: false,
                          });
                        } catch { return ''; }
                      };
                      let tooltip = `${d.date}${cell.source ? ` (${cell.source})` : ''}`;
                      if (cell.source === 'clock') {
                        const inT = fmtT(cell.clock_in_at);
                        const outT = fmtT(cell.clock_out_at);
                        const parts = [];
                        if (inT) parts.push(`Clocked in at ${inT}`);
                        if (outT) parts.push(`out at ${outT}`);
                        if (cell.hours != null) parts.push(`${cell.hours}h total`);
                        if (parts.length) tooltip = `${parts.join(' · ')} (UAE)`;
                      } else if (cell.source === 'manual' && cell.code) {
                        tooltip = `${d.date} — manual override`;
                      } else if (cell.source === 'leave') {
                        tooltip = `${d.date} — from leave record`;
                      }
                      return (
                        <td
                          key={d.date}
                          className="px-0.5 py-0.5 border-b text-center"
                          data-testid={`att-cell-${r.employee_code}-${d.date}`}
                        >
                          <div className="relative inline-block">
                            <select
                              value={cell.code || ''}
                              onChange={(e) => onCellChange(r.employee_id, d.date, e.target.value)}
                              title={tooltip}
                              className={`w-9 h-7 rounded border text-[10px] font-bold text-center appearance-none cursor-pointer transition hover:scale-105 focus:outline-none focus:ring-1 focus:ring-violet-400 ${color} ${isManual ? 'ring-1 ring-violet-400' : ''}`}
                              data-testid={`att-select-${r.employee_code}-${d.date}`}
                            >
                              <option value="">·</option>
                              {CODES.filter(c => c !== '').map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                            {cell.code === 'P' && cell.hours != null && (
                              <span
                                className="absolute -bottom-0.5 -right-0.5 leading-none px-0.5 rounded-sm bg-white/90 text-[7px] font-mono font-bold text-emerald-700 pointer-events-none shadow-sm border border-emerald-200"
                                title={`${cell.hours}h worked`}
                                data-testid={`att-hours-${r.employee_code}-${d.date}`}
                              >
                                {Number(cell.hours).toFixed(cell.hours >= 10 ? 0 : 1)}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AttendanceMatrix;
